import { CompanionCardHeader } from '@/components/companion/cards/CompanionCardHeader';
import { CompanionCardShell, EvidenceLine } from '@/components/companion/cards/CompanionCardShell';
import {
  formatCompanionDate,
  formatCompanionMoney,
} from '@/components/companion/feed/companion-formatters';
import { Stack, Text } from '@chakra-ui/react';
import type { CompanionActionDto, CompanionGiftUnclaimedEvidence } from '@fluenceur/types';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

/**
 * The unclaimed-gift card, as the companion panel shows it.
 *
 * The subject is the gift, not the affiliate — one person can be offered
 * several, and this card only ever speaks for the one the detector froze. The
 * deadline gets the same amber eyebrow as the display-rights card, because it
 * is the same kind of fact: a window that closes on its own if nobody acts.
 * The value is stated only when the evidence carries one figure with its
 * currency, never a guess built from a range.
 */

interface GiftUnclaimedCardProps {
  action: CompanionActionDto & { evidence: CompanionGiftUnclaimedEvidence };
  /** Opens the composer with this affiliate. Wired by the panel, never here. */
  onOpenCta: () => void;
  /** Starts the dismissal choice. Absent while the panel does not offer it. */
  onDismiss?: () => void;
  isCtaPending?: boolean;
  /** The workflow has no adapter in this build; the button is shown inert. */
  isCtaDisabled?: boolean;
  /** Fills the panel edge to edge: no frame of its own, the panel is the frame. */
  flat?: boolean;
  /** The host's own controls, rendered at the end of the action row. */
  footerEnd?: ReactNode;
}

export const GiftUnclaimedCard = ({
  action,
  onOpenCta,
  onDismiss,
  isCtaPending = false,
  isCtaDisabled = false,
  flat = false,
  footerEnd,
}: GiftUnclaimedCardProps) => {
  const { t, i18n } = useTranslation();
  const { evidence } = action;
  const language = i18n.language;

  const affiliateName =
    evidence.displayName ?? t('companion.cards.giftUnclaimed.unknownAffiliate');
  const value = evidence.giftValue ? formatCompanionMoney(evidence.giftValue, language) : null;

  return (
    <CompanionCardShell
      testId="companion-gift-unclaimed-card"
      dataAttrs={{ 'data-gift-request-id': evidence.giftRequestId }}
      flat={flat}
      ctaLabel={action.ctaLabel}
      onOpenCta={onOpenCta}
      onDismiss={onDismiss}
      isCtaPending={isCtaPending}
      isCtaDisabled={isCtaDisabled}
      footerEnd={footerEnd}
    >
      <CompanionCardHeader
        title={action.title}
        reason={action.reason}
        eyebrow={
          /* The deadline is the whole reason the card exists, so it is stated
             where the eye lands first and in the one colour the dashboard
             reserves for something running out. */
          <Text
            fontFamily="heading"
            fontSize="10.5px"
            fontWeight="700"
            letterSpacing="0.08em"
            textTransform="uppercase"
            color="warning.text"
            data-testid="gift-unclaimed-deadline"
          >
            {t('companion.cards.giftUnclaimed.deadline', { count: evidence.daysRemaining })}
          </Text>
        }
      >
        <Text fontSize="13px" color="ink" lineHeight="1.45">
          {affiliateName}
        </Text>
      </CompanionCardHeader>

      <Stack gap="2px">
        <EvidenceLine>
          {evidence.productTitle
            ? t('companion.cards.giftUnclaimed.product', { title: evidence.productTitle })
            : t('companion.cards.giftUnclaimed.productChoice', { count: evidence.productCount })}
        </EvidenceLine>
        <EvidenceLine>
          {t('companion.cards.giftUnclaimed.offeredAt', {
            date: formatCompanionDate(evidence.offeredAt, language),
          })}
        </EvidenceLine>
        <EvidenceLine>
          {t('companion.cards.giftUnclaimed.expiresAt', {
            date: formatCompanionDate(evidence.expiresAt, language),
          })}
        </EvidenceLine>
        {value ? (
          <EvidenceLine>{t('companion.cards.giftUnclaimed.value', { amount: value })}</EvidenceLine>
        ) : null}
      </Stack>
    </CompanionCardShell>
  );
};
